import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
	AUTH_TOKEN,
	USER_INFORMATION,
	USER_ROLE,
	DASHBOARD_NAV_TREE,
	PROTECTED_ROUTE,
	DEFAULT_ROUTE,
	USER_All_INFORMATION
} from 'constants/AuthConstant';
import AuthService from 'services/AuthService';

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	allowRedirect: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	verifyDeviceModal: false,
	resendDisabled: false,
	countdownTimer: 60,
}

export const signIn = createAsyncThunk('signIn', async (data, { rejectWithValue, dispatch, thunkAPI }) => {
	const { email, password } = data
	try {
		const response = await AuthService.login({ email, password, app_id: 1 });
		if (response?.code) {
			dispatch(verificationCode());
		} else if (response?.data?.user) {
			const token = response.data.access_token;
			dispatch(setLocalStorageUser(response.data));
			return {
				token,
				message: response.message
			};
		}
	} catch (err) {
		return rejectWithValue(err?.response?.data?.message);
	}
});

export const signUp = createAsyncThunk('signUp', async (data, { rejectWithValue, dispatch, thunkAPI }) => {
	const { email, password } = data
	try {
		const response = await AuthService.register({ email, password, app_id: 1 });
		return response;
	} catch (err) {
		return rejectWithValue(err?.response?.data.message);
	}
});

export const signOut = createAsyncThunk("signOut", async () => {
	localStorage.clear();
	const response = await AuthService.signOut();
	return response.data;
});

export const verifyUserDevice = createAsyncThunk('verifyUserDevice', async (data, { rejectWithValue, dispatch }) => {
	try {
		const response = await AuthService.verifyUserDevice(data)
		if (response?.data?.user) {
			dispatch(removeDeviceModal(false))
			const token = response.data.access_token;

			localStorage.setItem(AUTH_TOKEN, response.data.access_token);
			localStorage.setItem(USER_INFORMATION, JSON.stringify(response.data.user));
			localStorage.setItem(USER_All_INFORMATION, JSON.stringify(response.data.user_all_informations));
			localStorage.setItem(USER_ROLE, JSON.stringify(response.data.userRole));
			localStorage.setItem(DASHBOARD_NAV_TREE, JSON.stringify(response.data.dashBoardNavTree));
			localStorage.setItem(PROTECTED_ROUTE, JSON.stringify(response.data.protectedRoutes));
			localStorage.setItem(DEFAULT_ROUTE, JSON.stringify(response.data.defaultRoute));
			window.location.reload(true);

			return token;
		}
	} catch (err) {
		return rejectWithValue(err?.response?.data?.message);
	}
})

export const resendCode = createAsyncThunk('resendCode', async (data) => {
	try {
		await AuthService.resendCode(data)
	} catch (err) {
		throw err?.response?.data?.data?.message;
	}
})


export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setLocalStorageUser: (state, action) => {
			localStorage.clear();
			localStorage.setItem(AUTH_TOKEN, action.payload.access_token);
			localStorage.setItem(USER_INFORMATION, JSON.stringify(action.payload.user));
			localStorage.setItem(USER_All_INFORMATION, JSON.stringify(action.payload.user_all_informations));
			localStorage.setItem(USER_ROLE, JSON.stringify(action.payload.userRole));
			localStorage.setItem(DASHBOARD_NAV_TREE, JSON.stringify(action.payload.dashBoardNavTree));
			localStorage.setItem(PROTECTED_ROUTE, JSON.stringify(action.payload.protectedRoutes));
			localStorage.setItem(DEFAULT_ROUTE, JSON.stringify(action.payload.defaultRoute));
		},
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		},
		removeDeviceModal: (state, action) => {
			state.verifyDeviceModal = action.payload
		},
		verificationCode: (state, action) => {
			state.verifyDeviceModal = true
			state.resendDisabled = true
		},
		setResendDisabled: (state, action) => {
			state.resendDisabled = action.payload
		},
		updateCountDownTimer: (state, action) => {
			state.countdownTimer = action.payload
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true;
			})
			.addCase(signIn.fulfilled, (state, action) => {
				try{
					state.loading = false;
					state.redirect = "/";
					state.token = action.payload.token;
					state.message = action.payload.message;
				}catch(e){
					console.log(e)
				}
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			.addCase(signUp.pending, (state) => {
				state.loading = true;
				state.allowRedirect = false;
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = "/";
				state.message = action.payload.message;
				state.allowRedirect = action.payload.success;
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
				state.allowRedirect = false;
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false;
				state.token = null;
				state.redirect = "/";
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false;
				state.token = null;
				state.redirect = "/";
			})
			.addCase(verifyUserDevice.pending, (state) => {
				state.loading = true
			})
			.addCase(verifyUserDevice.fulfilled, (state, action) => {
				state.loading = false
				state.message = ''
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(verifyUserDevice.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
	},
})

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
	setLocalStorageUser,
	setResendDisabled,
	updateCountDownTimer,
	verificationCode,
	removeDeviceModal
} = authSlice.actions

export default authSlice.reducer